/**
 * PhotoAttach — the real photo picker behind every "Add photo" affordance
 * (helpdesk tickets, issue reports). Camera + library on native, file picker
 * on web (expo-image-picker handles both). Emits a base64 data-URI, matching
 * the API's photos column. Replaces the dashed AddPhotoBox placeholder.
 */
import * as ImagePicker from 'expo-image-picker';
import React from 'react';
import { Image, Platform, Pressable, View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText } from '@/ui';

const PICK_OPTS: ImagePicker.ImagePickerOptions = {
  mediaTypes: ['images'],
  base64: true,
  quality: 0.5,
  exif: false,
};

function toDataUri(asset: ImagePicker.ImagePickerAsset): string | null {
  if (!asset.base64) return null;
  const mime = asset.mimeType && asset.mimeType.startsWith('image/') ? asset.mimeType : 'image/jpeg';
  return `data:${mime};base64,${asset.base64}`;
}

export function PhotoAttach({ value, onChange, size = 88 }: {
  value: string | null;
  onChange: (dataUri: string | null) => void;
  size?: number;
}) {
  const { colors } = useTheme();
  const { t } = useT();
  const [error, setError] = React.useState<string | null>(null);

  const pick = async (source: 'camera' | 'library') => {
    setError(null);
    try {
      let result: ImagePicker.ImagePickerResult;
      if (source === 'camera') {
        const perm = await ImagePicker.requestCameraPermissionsAsync();
        if (!perm.granted) { setError(t('phatt_cam_declined')); return; }
        result = await ImagePicker.launchCameraAsync(PICK_OPTS);
      } else {
        result = await ImagePicker.launchImageLibraryAsync(PICK_OPTS);
      }
      if (result.canceled || !result.assets?.length) return;
      const uri = toDataUri(result.assets[0]);
      if (!uri) { setError(t('phatt_read_error')); return; }
      onChange(uri);
    } catch {
      setError(t('phatt_picker_error'));
    }
  };

  const boxStyle = {
    width: size, height: size, borderRadius: radius.md,
    alignItems: 'center' as const, justifyContent: 'center' as const,
    borderWidth: 1.5, borderStyle: 'dashed' as const,
    borderColor: colors.borderFirm, backgroundColor: colors.canvas1,
    gap: 4,
  };

  return (
    <View>
      <View style={{ flexDirection: 'row', gap: space.s3, alignItems: 'flex-start' }}>
        {value ? (
          <View>
            <Image
              source={{ uri: value }}
              style={{ width: size, height: size, borderRadius: radius.md, backgroundColor: colors.canvas2 }}
              resizeMode="cover"
            />
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('phatt_remove')}
              onPress={() => onChange(null)}
              hitSlop={8}
              style={{
                position: 'absolute', top: -8, right: -8, width: 24, height: 24, borderRadius: 12,
                backgroundColor: colors.textPrimary, alignItems: 'center', justifyContent: 'center',
              }}
            >
              <AppText variant="bodySm" weight="700" style={{ color: colors.canvas0, lineHeight: 16 }}>×</AppText>
            </Pressable>
          </View>
        ) : (
          <>
            <Pressable accessibilityRole="button" accessibilityLabel={t('phatt_choose')} onPress={() => void pick('library')} style={boxStyle}>
              <Icon name="plus" size={20} color={colors.textTertiary} />
              <AppText variant="label" tone="tertiary">{t('phatt_photos')}</AppText>
            </Pressable>
            {Platform.OS !== 'web' ? (
              <Pressable accessibilityRole="button" accessibilityLabel={t('take_photo')} onPress={() => void pick('camera')} style={boxStyle}>
                <Icon name="smartphone" size={20} color={colors.textTertiary} />
                <AppText variant="label" tone="tertiary">{t('phatt_camera')}</AppText>
              </Pressable>
            ) : null}
          </>
        )}
      </View>
      {error ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s2 }}>{error}</AppText> : null}
    </View>
  );
}
